import { jsx as _jsx } from "react/jsx-runtime";
import { getInjectionsForObjectType } from './tabInjectionRegistry';
import ComponentRenderer from '@helper/ComponentRenderer/ComponentRenderer';
function hasAccess(tab, userAppRoles, userGroupKey) {
    const tabRoles = tab.appRoles ? tab.appRoles.split(',').map(r => r.trim()).filter(Boolean) : [];
    const tabGroups = tab.userGroups ? tab.userGroups.split(',').map(g => g.trim()).filter(Boolean) : [];
    if (tabRoles.length === 0 && tabGroups.length === 0)
        return true;
    if (tabGroups.length > 0 && userGroupKey) {
        if (tabGroups.includes(userGroupKey))
            return true;
    }
    if (tabRoles.length > 0) {
        const allRoles = Object.values(userAppRoles || {}).flat();
        if (tabRoles.some(r => allRoles.includes(r)))
            return true;
    }
    return false;
}
function hasAccessInjection(injection, userAppRoles, userGroupKey) {
    const roles = injection.appRoles || [];
    const groups = injection.userGroups || [];
    if (roles.length === 0 && groups.length === 0)
        return true;
    if (groups.length > 0 && userGroupKey && groups.includes(userGroupKey))
        return true;
    if (roles.length > 0) {
        const allRoles = Object.values(userAppRoles || {}).flat();
        if (roles.some(r => allRoles.includes(r)))
            return true;
    }
    return false;
}
function buildComponentContent(components, passedProps) {
    const sorted = [...components].sort((a, b) => a.priority - b.priority);
    return (_jsx("div", { className: "uxpcore_injectedtab__content", children: sorted.map(comp => {
            var _a;
            // Component IDs are 3-part: moduleId/type/componentId or 2-part: moduleId/componentId
            const parts = comp.componentId.split('/');
            const moduleId = parts[0];
            const componentId = parts.length >= 3 ? parts[2] : parts[1];
            const configuredProps = (_a = comp.configuredProps) !== null && _a !== void 0 ? _a : {};
            const hasConfigured = Object.keys(configuredProps).length > 0;
            return (_jsx(ComponentRenderer, { moduleId: moduleId, componentId: componentId, configuredProps: configuredProps, hasConfigured: hasConfigured, additionalProps: passedProps, configNeededMessage: "Configure this widget's settings in the Page Editor" }, comp.componentId));
        }) }));
}
export function buildInjectedAdditionalDetails(options) {
    const { objectTabs, objectType, objectKey, parentProps = {}, userAppRoles = {}, userGroupKey = '' } = options;
    const mergedProps = Object.assign(Object.assign({}, parentProps), { objectKey, objectType });
    const key = objectType.toLowerCase();
    // DB-configured tabs (explicit priority + admin overrides)
    const dbTabs = (objectTabs === null || objectTabs === void 0 ? void 0 : objectTabs[key]) || [];
    const dbInjectionIds = new Set(dbTabs.map(t => { var _a; return (_a = t.contributionId) === null || _a === void 0 ? void 0 : _a.toLowerCase(); }).filter(Boolean));
    const configuredEntries = [...dbTabs]
        .filter(tab => tab.status !== 'Disabled')
        .sort((a, b) => a.priority - b.priority)
        .filter(tab => hasAccess(tab, userAppRoles, userGroupKey))
        .map(tab => ({
        id: tab.tabId,
        icon: tab.icon || 'fas puzzle-piece',
        label: tab.name,
        content: buildComponentContent(tab.components, mergedProps),
        refreshable: true,
    }));
    // Registry injections not yet in DB — auto-show with defaults
    const autoEntries = getInjectionsForObjectType(key)
        .filter(c => !dbInjectionIds.has(c.id.toLowerCase()))
        .filter(c => hasAccessInjection(c, userAppRoles, userGroupKey))
        .map(injection => ({
        id: injection.id,
        icon: injection.icon || 'fas puzzle-piece',
        label: injection.name,
        content: buildComponentContent(injection.components, mergedProps),
        refreshable: injection.refreshable !== false,
        events: injection.events,
    }));
    return [...configuredEntries, ...autoEntries];
}
